"use client";

import type { KundaliTranslations } from "./KundaliFormCard.i18n";
import { FieldError, FieldWrapper } from "./KundaliFormCard.fields";
import { formatIndianPhoneInput } from "@/lib/phone-india";

/**
 * Contact fields for the chat-entry / kundali form (India-first):
 *   - PhoneField   — phone is the strongest dedupe/delivery signal; required on
 *                    chat-entry surfaces. Fixed +91 prefix, numeric tel keypad,
 *                    autofocus on chat-entry, transactional WhatsApp reason.
 *   - EmailField   — always optional; validated only when non-empty.
 * Both persist via the existing captureReadingContact helper after the reading
 * is created (the form is the single decision — these are part of it, not a
 * second CTA, rule 39). The reading always renders regardless (rule 46).
 */

interface PhoneFieldProps {
  value: string;
  onChange: (v: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  activeField: string | null;
  t: KundaliTranslations;
  required: boolean;
  autoFocus: boolean;
  error?: string;
}

export function PhoneField({
  value,
  onChange,
  onFocus,
  onBlur,
  activeField,
  t,
  required,
  autoFocus,
  error,
}: PhoneFieldProps) {
  return (
    <FieldWrapper activeField={activeField} fieldName="phone">
      <label
        htmlFor="phone"
        className="block text-[13px] font-semibold mb-1"
        style={{ color: "var(--ish-dark)" }}
      >
        {t.phone}
      </label>
      <div className="flex items-stretch">
        <span
          aria-hidden
          className="flex items-center rounded-l-lg px-3 text-[14px] font-medium select-none"
          style={{
            border: "1px solid var(--ish-border)",
            borderRight: "none",
            background: "var(--ish-bg)",
            color: "var(--ish-text-muted)",
          }}
        >
          +91
        </span>
        <input
          id="phone"
          name="phone"
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          autoFocus={autoFocus}
          required={required}
          value={value}
          onChange={(e) => onChange(formatIndianPhoneInput(e.target.value))}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder={t.phonePlaceholder}
          maxLength={10}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? "phone-error" : "phone-reason"}
          className="input-sacred w-full rounded-r-lg px-4 py-3 text-[14px]"
          style={{ color: "var(--ish-dark)" }}
        />
      </div>
      <p
        id="phone-reason"
        className="mt-1.5 text-[11px]"
        style={{ color: "var(--ish-text-muted)" }}
      >
        {t.phoneReason}
      </p>
      <FieldError id="phone-error" message={error} />
    </FieldWrapper>
  );
}

interface EmailFieldProps {
  value: string;
  onChange: (v: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  activeField: string | null;
  t: KundaliTranslations;
  error?: string;
}

export function EmailField({
  value,
  onChange,
  onFocus,
  onBlur,
  activeField,
  t,
  error,
}: EmailFieldProps) {
  return (
    <FieldWrapper activeField={activeField} fieldName="email">
      <label
        htmlFor="email"
        className="block text-[13px] font-semibold mb-1"
        style={{ color: "var(--ish-dark)" }}
      >
        {t.email}{" "}
        <span className="font-normal" style={{ color: "var(--ish-text-faint)" }}>
          {t.optional}
        </span>
      </label>
      <input
        id="email"
        name="email"
        type="email"
        inputMode="email"
        autoComplete="email"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={t.emailPlaceholder}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? "email-error" : undefined}
        className="input-sacred w-full rounded-lg px-4 py-3 text-[14px]"
        style={{ color: "var(--ish-dark)" }}
      />
      <FieldError id="email-error" message={error} />
    </FieldWrapper>
  );
}
